import { useEffect, useRef } from "react";
import {
  TICKS_PER_SECOND,
  TOWER_KINDS,
  applyFog,
  idx,
  isCore,
  towerStats,
  type BattleResult,
  type Layout,
  type TowerKind,
} from "@empire/core-sim";
import { BoardView } from "../../pixi/BoardView.js";
import { BoardNotice } from "../components/BoardNotice.js";
import { towerUnavailable, useEditorStore, type Brush } from "../../store/editor.js";
import { TOWER_BLURB, unitKindsFromPlan } from "../theme/glyphs.js";
import { roadAllowance } from "../../tutorial/steps.js";
import {
  TUTORIAL,
  goalState,
  surveyComplete,
  useTutorialStore,
  type SurveyAnswers,
} from "../../store/tutorial.js";

/**
 * The first ten minutes (S6-1) and the survey that decides Phase 0 (S6-2).
 *
 * Full screen with no tab bar, per 03 §5: while somebody is learning what the
 * game IS, giving them four other places to go is how they leave.
 *
 * Two things here were broken badly enough to make the whole tutorial read as
 * broken, and both are fixed below:
 *  - the board was never told about the player's edits, so drawing a road in
 *    the build step produced nothing on screen at all;
 *  - the build step offered no tools, while asking for a road and two towers.
 */
export function Tutorial(): JSX.Element | null {
  const active = useTutorialStore((s) => s.active);
  const phase = useTutorialStore((s) => s.phase);

  if (!active) return null;
  if (phase === "survey") return <Survey />;

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-bg">
      <Progress />
      <Tools />
      <Stage />
      <Coach />
    </div>
  );
}

function Progress(): JSX.Element {
  const index = useTutorialStore((s) => s.index);
  const skip = useTutorialStore((s) => s.skip);

  return (
    <header className="flex shrink-0 items-center gap-3 border-b border-panel-edge bg-bg-raised px-4 py-3">
      <div className="flex gap-1.5">
        {TUTORIAL.map((step, i) => (
          <span
            key={step.id}
            className={`h-1.5 w-6 rounded-full ${i <= index ? "bg-gold" : "bg-panel-edge"}`}
          />
        ))}
      </div>
      <button
        type="button"
        data-testid="tutorial-skip"
        onClick={skip}
        className="ml-auto h-11 rounded-lg px-3 text-[11px] font-semibold text-ink-dim hover:text-ink"
      >
        Skip
      </button>
    </header>
  );
}

const TOOL_LABELS: Record<Brush, string> = {
  road: "Road",
  mud: "Mud",
  barricade: "Barricade",
  tower_slot: "Slot",
  gate: "Gate",
  select: "Towers",
  erase: "Erase",
};

/**
 * The tools this step hands over — the same editor tools, not a copy.
 *
 * A step lists what it teaches and nothing else, so the road beat cannot be
 * failed by having the Erase tool selected, and the player is never looking at
 * a button whose purpose has not been explained yet.
 */
function Tools(): JSX.Element | null {
  const index = useTutorialStore((s) => s.index);
  const phase = useTutorialStore((s) => s.phase);
  const brush = useEditorStore((s) => s.brush);
  const setBrush = useEditorStore((s) => s.setBrush);
  const setTowerBrush = useEditorStore((s) => s.setTowerBrush);
  const step = TUTORIAL[index];

  // Pick up this step's first tool as it opens, so nothing has to be selected
  // before the very first tap works. On the towers step that includes the
  // tower itself: an Arrow is armed, so the first tap beside the road builds.
  useEffect(() => {
    const first = step?.tools?.[0];
    if (first) setBrush(first);
    if (step?.goal === "towers") setTowerBrush("arrow");
  }, [index, step, setBrush, setTowerBrush]);

  if (!step?.tools || phase !== "playing") return null;

  return (
    <div className="shrink-0 border-b border-panel-edge bg-bg-raised">
      <div className="flex gap-1 overflow-x-auto no-scrollbar px-2 py-2">
        {step.tools.map((tool) => (
          <button
            key={tool}
            type="button"
            data-testid={`tutorial-tool-${tool}`}
            onClick={() => setBrush(tool)}
            className={`h-11 whitespace-nowrap rounded-lg px-3.5 text-xs font-semibold transition-colors ${
              brush === tool ? "bg-gold text-bg" : "bg-panel text-ink-dim hover:text-ink"
            }`}
          >
            {TOOL_LABELS[tool]}
          </button>
        ))}
      </div>
      {brush === "select" ? <TowerTray /> : null}
    </div>
  );
}

/** The tower types this board can actually build, armed by tapping. */
export function TowerTray(): JSX.Element {
  const layout = useEditorStore((s) => s.layout);
  const towerBrush = useEditorStore((s) => s.towerBrush);
  const setTowerBrush = useEditorStore((s) => s.setTowerBrush);

  return (
    <div className="flex gap-1 overflow-x-auto no-scrollbar border-t border-panel-edge px-2 py-2">
      {TOWER_KINDS.map((kind: TowerKind) => {
        const stats = towerStats(kind, 1);
        const blocked = towerUnavailable(layout, kind);
        const armed = towerBrush === kind;
        return (
          <button
            key={kind}
            type="button"
            disabled={blocked !== null}
            data-testid={`tutorial-tower-${kind}`}
            onClick={() => setTowerBrush(armed ? null : kind)}
            className={`min-w-[112px] rounded-lg px-3 py-1.5 text-left transition-colors ${
              armed ? "bg-gold text-bg" : "bg-panel text-ink-dim enabled:hover:text-ink"
            } disabled:opacity-35`}
          >
            <span className="block text-xs font-semibold">{stats.name}</span>
            <span className="block text-[10px] opacity-80">
              {blocked ?? TOWER_BLURB[kind as TowerKind]}
            </span>
            {blocked ? null : (
              <span className="block text-[10px] tabular-nums opacity-60">
                range {stats.rangeMilli / 1000}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** How much faster than real time the tutorial's battle plays. */
const REPLAY_SPEED = 1.5;

/** The board, and the units when a battle has been resolved. */
function Stage(): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<BoardView | null>(null);
  const index = useTutorialStore((s) => s.index);
  const step = TUTORIAL[index]!;
  const layout = useEditorStore((s) => s.layout);
  const validation = useEditorStore((s) => s.validation);
  const selected = useEditorStore((s) => s.selected);
  const pending = useEditorStore((s) => s.pending);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;
    let view: BoardView | undefined;
    let position = 0;

    // T3 is the player's first attack, and the lesson is fog: they may only
    // see the gate and the core going in, and afterwards only what their
    // units walked past. It used to show the whole maze while the coach said
    // "the rest stays hidden".
    const startingBoard =
      step.beat === "build"
        ? useEditorStore.getState().layout
        : step.beat === "attack"
          ? blindView(step.layout).layout
          : step.layout;
    let revealed = false;

    // Build steps are seen from straight above, like the real editor.
    void BoardView.mount(host, startingBoard, { topDown: step.beat === "build" }).then((mounted) => {
      if (cancelled) {
        mounted.destroy();
        return;
      }
      view = mounted;
      viewRef.current = mounted;
      mounted.setUnitKinds(unitKindsFromPlan(step.plan));
      // Watching or attacking a set maze: frame the maze, not the empty board.
      // Build steps keep the whole board — the player needs room to draw.
      if (step.beat === "attack") mounted.setFog(blindView(step.layout).fog);
      // Build steps get room around what matters — the gate the coach points
      // at and the core — rather than the whole 12×12 board, whose tiles are
      // about half a finger wide on a phone.
      const narrow = mounted.app.screen.width < 600;
      const frameBuild = (): void =>
        mounted.frameAction(1, step.focus ? [step.focus] : [], 1, narrow ? mounted.proj.fingerTilePx : 0);
      if (step.beat !== "build") mounted.frameAction();
      else frameBuild();
      // The mark the coach points at. The effect below that normally draws it
      // runs before this async mount has finished, so on the first render it
      // had no board to draw on — "tap the marked tile" pointed at nothing.
      // Only build steps have a selection; a leftover one from T2 must not
      // follow the player into T3's attack.
      mounted.setSelection(step.beat === "build" ? useEditorStore.getState().selected : null, null);
      mounted.setHint(
        step.focus && !goalState(step, useEditorStore.getState().layout).done ? step.focus : null
      );
      let framedBattle = false;

      // On a build step the player draws on this board with the real editor
      // handlers — the same ones the defence editor uses, so nothing they learn
      // here has to be unlearned later.
      if (step.beat === "build") {
        mounted.on({
          onPaint: (at) => useEditorStore.getState().paint(at),
          onPaintEnd: () => useEditorStore.getState().commit(),
          // No long-press here. It converts road to mud or a barricade, which
          // the tutorial never teaches — and a press that rested on the gate
          // for half a second before dragging turned the gate into a
          // barricade and stopped the road from drawing at all.
        });
      }

      mounted.app.ticker.add((ticker) => {
        const { frames, phase, watching } = useTutorialStore.getState();
        if (phase !== "resolved" || frames.length === 0) {
          mounted.setReplay(null);
          position = 0;
          if (revealed) {
            revealed = false;
            const blind = blindView(step.layout);
            mounted.setLayout(blind.layout);
            mounted.setFog(blind.fog);
          }
          // Back to building after a battle: give the player the whole board again.
          if (framedBattle) {
            framedBattle = false;
            frameBuild();
          }
          return;
        }
        // The attack is resolved: now they know what they walked past.
        const { result } = useTutorialStore.getState();
        if (step.beat === "attack" && result && !revealed) {
          revealed = true;
          const seen = foggedView(step.layout, result);
          mounted.setLayout(seen.layout);
          mounted.setFog(seen.fog);
        }
        // A battle on a build step: zoom to the maze the player just drew.
        if (step.beat === "build" && !framedBattle) {
          framedBattle = true;
          mounted.frameAction();
        }
        const end = frames.length - 1;
        // Skipped: jump to the end, where the survivors and the rubble are.
        // A little faster than real time: the tutorial is meant to take a
        // minute, and the battle is most of it.
        position = watching
          ? Math.min(position + (ticker.deltaMS * REPLAY_SPEED) / (1000 / TICKS_PER_SECOND), end)
          : end;
        mounted.setReplay(frames, position, watching);
        if (watching && position >= end) useTutorialStore.getState().finishWatching();
      });
    });

    return () => {
      cancelled = true;
      viewRef.current = null;
      view?.destroy();
    };
  }, [index, step]);

  /**
   * Push every edit into the view.
   *
   * This is the line whose absence made the build step look broken: the player
   * dragged a finger across the board and nothing appeared, because the view
   * only ever redraws when it is handed a layout.
   */
  useEffect(() => {
    if (step.beat !== "build") return;
    viewRef.current?.setLayout(layout, validation.ok ? [] : validation.error.tiles);
  }, [layout, validation, step]);

  /** Mark the tile the coach is pointing at, or whatever the player selected. */
  useEffect(() => {
    const building = step.beat === "build";
    viewRef.current?.setPending(building ? pending : null);
    viewRef.current?.setSelection(building ? (pending ?? selected ?? null) : null, null);
    // The pointer stays until the thing it points at is done.
    viewRef.current?.setHint(step.focus && !goalState(step, layout).done ? step.focus : null);
  }, [selected, pending, step, layout]);

  return (
    <div className="relative min-h-0 flex-1 overflow-hidden">
      <div
        ref={hostRef}
        className="h-full w-full touch-none [&>canvas]:block"
        data-testid="tutorial-board"
      />
      {step.beat === "build" ? <BuildHud /> : null}
      {step.beat === "build" ? <BoardNotice /> : null}
    </div>
  );
}

/** The two numbers that matter while building: road tiles, and towers up. */
function BuildHud(): JSX.Element {
  const layout = useEditorStore((s) => s.layout);
  const road = roadAllowance(layout);
  const towers = layout.tiles.filter((t) => t.tower).length;

  return (
    <div
      className="pointer-events-none absolute left-3 top-3 rounded bg-black/50 px-2 py-1 text-[11px] tabular-nums text-ink-dim"
      data-testid="tutorial-budget"
    >
      road {road.used}/{road.allowed} · towers {towers}
    </div>
  );
}

/** One sentence, and one button. Never a page of text (03 §5). */
function Coach(): JSX.Element {
  const index = useTutorialStore((s) => s.index);
  const phase = useTutorialStore((s) => s.phase);
  const result = useTutorialStore((s) => s.result);
  const nudge = useTutorialStore((s) => s.nudge);
  const attemptsLeft = useTutorialStore((s) => s.attemptsLeft);
  const resolve = useTutorialStore((s) => s.resolve);
  const advance = useTutorialStore((s) => s.advance);
  const next = useTutorialStore((s) => s.next);
  const retry = useTutorialStore((s) => s.retry);
  const watching = useTutorialStore((s) => s.watching);
  const finishWatching = useTutorialStore((s) => s.finishWatching);
  const layout = useEditorStore((s) => s.layout);

  const step = TUTORIAL[index]!;
  const failedBuild =
    phase === "resolved" && step.beat === "build" && result?.outcome === "breach";
  const ready = goalState(step, layout).done;

  // A step with a battle ends by fighting it; the teaching steps end with Done.
  const fights = step.beat !== "build" || step.goal === "battle";
  const label =
    step.beat === "watch" ? "Watch" : step.beat === "attack" ? "Attack" : fights ? "Send them in" : "Done";

  return (
    <div
      className="shrink-0 border-t border-panel-edge bg-bg-raised px-4 py-3"
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      <div className="flex items-start gap-3">
        {/* Placeholder coach: a shape with a letter, until art exists (S6-3). */}
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gold font-display text-lg text-bg">
          E
        </span>
        <p
          className={`min-w-0 flex-1 text-sm ${nudge ? "text-warn" : "text-ink"}`}
          data-testid="coach"
        >
          {nudge ??
            (phase === "resolved" && watching
              ? step.coach
              : phase === "resolved"
                ? line(step.after, result, failedBuild, attemptsLeft)
                : step.coach)}
        </p>
      </div>

      <div className="mt-3 flex gap-2">
        {phase === "resolved" && watching ? (
          <button
            type="button"
            data-testid="tutorial-skip-replay"
            onClick={finishWatching}
            className="h-11 flex-1 rounded-lg border border-panel-edge bg-panel text-xs font-semibold text-ink-dim hover:text-ink"
          >
            Skip to the result
          </button>
        ) : phase === "playing" ? (
          // Never disabled. If the step is not finished, the coach says why —
          // a dead button with a tooltip is invisible on a phone.
          <button
            type="button"
            data-testid="tutorial-go"
            onClick={fights ? resolve : advance}
            className={`h-11 flex-1 rounded-lg text-xs font-bold ${
              ready ? "bg-gold text-bg" : "bg-panel text-ink-dim"
            }`}
          >
            {label}
          </button>
        ) : failedBuild && attemptsLeft > 0 ? (
          <>
            <button
              type="button"
              data-testid="tutorial-retry"
              onClick={retry}
              className="h-11 flex-1 rounded-lg bg-gold text-xs font-bold text-bg"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={next}
              className="h-11 flex-1 rounded-lg border border-panel-edge bg-panel text-xs font-semibold text-ink-dim hover:text-ink"
            >
              Move on
            </button>
          </>
        ) : (
          <button
            type="button"
            data-testid="tutorial-next"
            onClick={next}
            className="h-11 flex-1 rounded-lg bg-gold text-xs font-bold text-bg"
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}

function line(
  after: string,
  result: { outcome: string; army: { reached: number; sent: number } } | null,
  failedBuild: boolean,
  attemptsLeft: number
): string {
  if (failedBuild && attemptsLeft > 0 && result) {
    return `${result.army.reached} of ${result.army.sent} got through. Try a longer road.`;
  }
  if (failedBuild && result) {
    return `${result.army.reached} of ${result.army.sent} got through. That happens — keep going.`;
  }
  return after;
}

/**
 * The three questions the Phase 0 gate is made of (01 §5).
 *
 * Both ratings must average 3.5 or better and 60% must want a second battle,
 * or the battle design goes back for another round. They are asked here, before
 * the village reveal, while the battles are still the last thing the player saw
 * — asked afterwards, they measure how somebody feels about a village.
 */
function Survey(): JSX.Element {
  const answers = useTutorialStore((s) => s.answers);
  const answer = useTutorialStore((s) => s.answer);
  const submit = useTutorialStore((s) => s.submitSurvey);
  const skipSurvey = useTutorialStore((s) => s.skipSurvey);

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-bg">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
        <h2 className="font-display text-2xl text-gold">Three quick questions</h2>
        <p className="mt-1 text-[11px] text-ink-dim">
          Honest answers help more than kind ones. Nothing here is shared.
        </p>

        <Rating
          label="Attacking somebody's maze was…"
          value={answers.attacking}
          onPick={(attacking) => answer({ attacking })}
          testId="rate-attacking"
        />
        <Rating
          label="Building a defence was…"
          value={answers.defending}
          onPick={(defending) => answer({ defending })}
          testId="rate-defending"
        />

        <p className="mt-6 text-sm text-ink">Would you play a second battle?</p>
        <div className="mt-2 flex gap-2">
          {[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ].map((option) => (
            <button
              key={option.label}
              type="button"
              data-testid={`second-${option.label.toLowerCase()}`}
              onClick={() => answer({ secondBattle: option.value })}
              className={`h-11 flex-1 rounded-lg text-xs font-semibold ${
                answers.secondBattle === option.value
                  ? "bg-gold text-bg"
                  : "border border-panel-edge bg-panel text-ink-dim"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="shrink-0 border-t border-panel-edge bg-bg-raised px-5 py-3"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        {surveyComplete(answers) ? null : (
          // A disabled button with no reason reads as a frozen screen.
          <p className="mb-2 text-center text-[11px] text-ink-dim" data-testid="survey-hint">
            Answer all three to continue.
          </p>
        )}
        <button
          type="button"
          data-testid="survey-submit"
          disabled={!surveyComplete(answers)}
          onClick={() => void submit()}
          className="h-11 w-full rounded-lg bg-gold text-xs font-bold text-bg disabled:opacity-40"
        >
          Send
        </button>
        <button
          type="button"
          data-testid="survey-skip"
          onClick={skipSurvey}
          className="mt-1 h-10 w-full text-[11px] font-semibold text-ink-dim"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

function Rating({
  label,
  value,
  onPick,
  testId,
}: {
  label: string;
  value: number | null;
  onPick: (value: number) => void;
  testId: string;
}): JSX.Element {
  return (
    <div className="mt-6">
      <p className="text-sm text-ink">{label}</p>
      <div className="mt-2 flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`${testId}-${n}`}
            onClick={() => onPick(n)}
            className={`h-11 flex-1 rounded-lg text-xs font-bold ${
              value === n ? "bg-gold text-bg" : "border border-panel-edge bg-panel text-ink-dim"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-ink-dim opacity-70">
        <span>dull</span>
        <span>great</span>
      </div>
    </div>
  );
}

export type { SurveyAnswers };

/** A layout with everything the attacker cannot see removed, and those squares to fog. */
function masked(layout: Layout, visible: (index: number) => boolean): { layout: Layout; fog: number[] } {
  const fog: number[] = [];
  const tiles = layout.tiles.map((tile, i) => {
    if (visible(i)) return tile;
    fog.push(i);
    return { kind: "empty" as const };
  });
  return { layout: { ...layout, tiles }, fog };
}

/** Before the first attack: the gate they chose and the core everyone knows. */
function blindView(layout: Layout): { layout: Layout; fog: number[] } {
  const gate = idx(layout.gate.x, layout.gate.y, layout.size);
  return masked(layout, (i) => i === gate || isCore(i % layout.size, Math.floor(i / layout.size)));
}

/** After it: exactly what the server would have sent a real attacker (D-028). */
function foggedView(layout: Layout, result: BattleResult): { layout: Layout; fog: number[] } {
  const view = applyFog(layout, result);
  return masked(layout, (i) => view.tiles[i] !== null);
}
